import React, { useMemo } from 'react';
import { Card } from 'react-bootstrap';
import { getItem } from '../helper/localstorage.helper';
import { formatNumberWithCommas, formatNumberSwedish } from '../helper/salary.helpers';

interface SalaryData {
  net?: string | number;
  amountIncludingVat?: string | number;
  vat?: string | number;
  vatPercentage?: string | number;
  amount?: string | number;
  fee?: string | number;
  socialFee?: string | number;
  feeInSEK?: string | number;
  grossInSek?: string | number;
  tax?: string | number;
  taxInSek?: string | number;
  socialFeeInSek?: string | number;
  toPay?: string | number;
  toPayInSek?: string | number;
  socialFeeRateDeduction?: number;
  taxRateDeduction?: number;
  fakturaFeePercentage?: number;
  workingHours: Array<{ amount?: number; }>;
  pension: string | number;
  pensionSocialFee: string | number;
  holidayPayAmount?: string | number;
  pensionInSek?: string | number;
  holidayPayAmountInSek?: string | number;
  currencyMarginPercentage?: string | number;
  currencyMarginAmount?: string | number;
  netInSek?: string | number;
}

interface SalaryResultCardProps {
  t: (key: string) => string;
  data: SalaryData;
  currencyName: string;
  activeTab: 'invoice' | 'gross' | 'net';
  selectedVat: string | number;
  travelVat: string | number;
  materialsToolsVat: string | number;
  customExpenses: Array<{ id: number; type: string; amount: number; vat: number }>;
}

const RowItem: React.FC<{ label: string; value: string }> = React.memo(({ label, value }) => (
  <div className="d-flex justify-content-between align-items-start mb-2">
    <span className="section-title flex-shrink-1 pe-2" style={{ wordBreak: 'break-word' }}>{label}</span>
    <span className="value flex-shrink-0 text-end" style={{ minWidth: 'fit-content' }}>{value}</span>
  </div>
));

RowItem.displayName = 'RowItem';

// Utility function to sum all workingHours entries by description and apply VAT
export function sumByDescriptionWithVat(
  workingHours: Array<{ amount?: number; workingDescription?: string }> = [],
  desc: string,
  vat: string | number
): number {
  if (!Array.isArray(workingHours)) return 0;
  const sum = workingHours
    .filter(
      exp => exp && exp.workingDescription === desc && typeof exp.amount === 'number'
    )
    .reduce((acc, exp) => acc + (exp.amount ?? 0), 0);
  const v = typeof vat === 'number' ? vat : Number(vat);
  if (isNaN(sum) || isNaN(v)) return 0;
  return sum + (sum * v / 100);
}


const SalaryResultCard: React.FC<SalaryResultCardProps> = ({ t, data, currencyName, activeTab, selectedVat, travelVat, materialsToolsVat, customExpenses }) => {
  const {
    net = "00",
    amountIncludingVat = "00",
    vat = "00",
    vatPercentage = "0",
    amount = "00",
    fee = "00",
    socialFee = "00",
    feeInSEK = "00",
    grossInSek = "00",
    tax = "00",
    taxInSek = "00",
    socialFeeInSek = "00",
    toPay = "00",
    socialFeeRateDeduction = 0,
    taxRateDeduction = 0,
    fakturaFeePercentage = 0,
    pension = "00",
    pensionSocialFee = "00",
    holidayPayAmount = "00",
    pensionInSek = "00",
    holidayPayAmountInSek = "00",
    toPayInSek = "00",
    currencyMarginPercentage = "00",
    currencyMarginAmount = "00",
    netInSek = "00",
    workingHours = Array<{ amount?: number; }>(),
  } = data || {};

  const { isSwedish, format } = useMemo(() => {
    const isSwedish = getItem('i18nextLng') === 'sv-se';
    const format = (val: string | number) =>
      ` ${isSwedish ? formatNumberSwedish(val) : formatNumberWithCommas(val)}`;
    return { isSwedish, format };
  }, []);

  const { noteTitle, noteDesc } = useMemo(() => {
    const noteText = t('NOTE_ESTIMATE');
    const [title = '', desc = ''] = noteText.split(':');
    return { noteTitle: title, noteDesc: desc };
  }, [t]);

  const { headingText, mainValue } = useMemo(() => {
    const headingText = (() => {
      switch (activeTab) {
        case 'invoice':
          return t('INVOICE_AMOUNT');
        case 'gross':
          return t('GROSS_SALARY_RESULT');
        default:
          return t('NET_SALARY_RESULT');
      }
    })();

    const mainValue = (() => {
      switch (activeTab) {
        case 'invoice':
          return `${amount} ${t('kr')}`;
        case 'net':
          return `SEK ${format(currencyName)=== "SEK" ?format(net) : format(netInSek)}`;
        default:
          return `SEK ${format(grossInSek)}`;
      }
    })();

    return { headingText, mainValue };
  }, [activeTab, t, currencyName, amount, net, grossInSek]);

  const feeMapping: Record<number, number> = {
    23.91: 31.42,
    9.26: 10.21,
    0: 0
  };


  return (
    <Card className="salary-card margin-left-166">
      <h4 className="mb-2 salary-heading text-break">{headingText}</h4>
      <h3 className="Heading_h3 pb-4 text-break">{format(mainValue)}</h3>

      <div className="salary-line pt-4" />

      <section>
        <h6 className="salary-section-title pb-4 mb-0 pt-2">{t('INVOICE_DETAILS')}</h6>
        <RowItem
          label={t('INVOICE_AMOUNT_INC_VAT')}
          value={`${format(amountIncludingVat)} ${t('kr')}`}
        />
        <RowItem
          label={`${t('VAT_PERCENT')} (${vatPercentage}%)`}
          value={`${format(vat)} ${t('kr')}`}
        />
        <RowItem
          label={t('INVOICE_AMOUNT_EXC_VAT')}
          value={ `${format(amount)} ${t('kr')}`}
        />
      </section>

      <div className="salary-line pt-4" />

      <section>
        <h6 className="salary-section-title pb-4 mb-0">{t('FEES')}</h6>
        <RowItem
          label={`${t('INVOIZO_FEE')} (${fakturaFeePercentage}%)`}   
          value={`${format(currencyName).includes("SEK") ? format(fee)  : format(feeInSEK)} ${t('kr')}`}        />
        {currencyName !== "SEK" && (
         <RowItem
          label={`${t('INVOIZO_CURRENCY_MARGIN')} (${currencyMarginPercentage}%)`}
          value={` ${format(currencyMarginAmount)} ${t('kr')}`}
        />)}
        <RowItem
          label={`${t('SOCIAL_FEES')} (${feeMapping[socialFeeRateDeduction] ?? socialFeeRateDeduction}%)`}
          value={` ${format(currencyName).includes("SEK") ? format(socialFee) : format(socialFeeInSek)} ${t('kr')}`}
        />
      </section>

      <div className="salary-line pt-4" />


      <section>

        <h3 className="salary-section-title pb-4 mb-0">{t('SALARY_COMPONENTS')}</h3>
        <RowItem
          label={t('GROSS_SALARY_HOLIDAY')}
          value={`${format(grossInSek)} ${t('kr')}`}
        />
        <div className="salary-line pt-4" />

        <h3 className="salary-section-title pb-4 mb-0">{t('TAX_INFORMATION')}</h3>
        <RowItem
          label={`${t('INCOME_TAX')} (${taxRateDeduction}%)`}
          value={`${format(currencyName)=== "SEK" ?format(tax) : format(taxInSek)} ${t('kr')}`}
        />
        <div className="salary-line pt-4" />


        <h3 className="salary-section-title pb-4 mb-0">{t('Holiday allowance')}</h3>
        <RowItem
          label={t('Holiday Fee')}
          value={`${format(currencyName)=== "SEK" ?format(holidayPayAmount) : format(holidayPayAmountInSek)} ${t('kr')}`}
        />
        <div className="salary-line pt-4" />

        <h3 className="salary-section-title pb-4 mb-0">{t('Pension')}</h3>
        <RowItem
          label={t('Pension Social Fee')}
          value={`${format(pensionSocialFee)} ${t('kr')}`}
        />
        <RowItem
          label={t('Pension Amount')}
          value={`${format(currencyName)=== "SEK" ?format(pension) : format(pensionInSek)} ${t('kr')}`}
        />
        <div className="salary-line pt-4" />

        {activeTab.includes('invoice') && (
          <>
            {/* <h3 className="salary-section-title pb-4 mb-0">{t('ALLOWANCES')}</h3> */}
            <RowItem
              label={t('Mileage Report')}
              value={`${format(
                sumByDescriptionWithVat(workingHours, 'Mileage Report', selectedVat)
              )} ${t('kr')}`}
            />
            <RowItem
              label={t('Allowance in Sweden')}
              value={`${format(
                sumByDescriptionWithVat(workingHours, 'Allowance in Sweden', selectedVat)
              )} ${t('kr')}`}
            />
            <RowItem
              label={t('Allowance outside Sweden')}
              value={`${format(
                sumByDescriptionWithVat(workingHours, 'Allowance outside Sweden', selectedVat)
              )} ${t('kr')}`}
            />
            <RowItem
              label={t('Travel Expenses')}
              value={`${format(
                sumByDescriptionWithVat(workingHours, 'Travel Expenses as per attached PDF',
                  (typeof travelVat === 'number' ? travelVat : Number(travelVat)) +
                  customExpenses.reduce((acc, v) => acc + (typeof v.vat === 'number' ? v.vat : Number(v.vat)), 0)

                )
              )} ${t('kr')}`}
            />
            <RowItem
              label={t('Expenses for materials and tools')}
              value={`${format(
                // Sum standard material/tools
                sumByDescriptionWithVat(
                  workingHours,
                  'Expenses for material and tools as per attached pdf',
                  (typeof materialsToolsVat === 'number' ? materialsToolsVat : Number(materialsToolsVat)) +
                  customExpenses.reduce((acc, v) => acc + (typeof v.vat === 'number' ? v.vat : Number(v.vat)), 0)
                )
              )} ${t('kr')}`}
            />
          </>
        )}
      </section>

      <div className="salary-line pt-4" />

      <div className="d-flex justify-content-between align-items-start">
        <h3 className="salary-section-title flex-shrink-1 pe-2">{t('NET_SALARY_RESULT')}</h3>
        <h3 className="value flex-shrink-0">{`${format(currencyName)=== "SEK" ? format(toPay) : format(toPayInSek)} ${t('kr')}`}</h3>
      </div>

      <div className="note w-75">
        <b className="salary-note-bold">{noteTitle}:</b> {noteDesc}
      </div>
    </Card>
  );
};

export default SalaryResultCard;
